import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'contracts', label: 'Contracts' },
  { value: 'proposals', label: 'Proposals' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'reports', label: 'Reports' },
  { value: 'legal', label: 'Legal' },
  { value: 'other', label: 'Other' }
];

const ENTITY_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'lead', label: 'Lead' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'contact', label: 'Contact' }
];

const UploadDocumentModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    entity_type: 'client',
    entity_id: '',
    category: 'other',
    description: '',
    tags: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (isOpen) {
      fetchEntities(formData.entity_type);
    }
  }, [isOpen, formData.entity_type]);

  const fetchEntities = async (entityType) => {
    try {
      setLoading(true);
      let endpoint = '';
      
      switch (entityType) {
        case 'client':
          endpoint = '/api/crm/clients';
          break;
        case 'lead':
          endpoint = '/api/crm/leads';
          break;
        case 'opportunity':
          endpoint = '/api/crm/opportunities';
          break;
        case 'contact':
          endpoint = '/api/crm/contacts';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      setEntities(data);
      
      // Auto-select first entity if available
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, entity_id: data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entities',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (16MB max)
      const maxSize = 16 * 1024 * 1024;
      if (file.size > maxSize) {
        toast({
          title: 'Error',
          description: 'File size exceeds 16MB limit',
          variant: 'destructive'
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!selectedFile) {
      toast({
        title: 'Validation Error',
        description: 'Please select a file to upload',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.entity_id) {
      toast({
        title: 'Validation Error',
        description: 'Please select an entity',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploading(true);

      // Create form data
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      uploadData.append('entity_type', formData.entity_type);
      uploadData.append('entity_id', formData.entity_id);
      uploadData.append('category', formData.category);
      
      if (formData.description) {
        uploadData.append('description', formData.description);
      }
      
      if (formData.tags) {
        uploadData.append('tags', formData.tags);
      }

      const response = await fetch(`${API_URL}/api/crm/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload document');
      }

      toast({
        title: 'Success',
        description: 'Document uploaded successfully'
      });

      // Reset form
      setFormData({
        entity_type: 'client',
        entity_id: '',
        category: 'other',
        description: '',
        tags: ''
      });
      setSelectedFile(null);

      onSuccess();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const getEntityLabel = (entity) => {
    if (formData.entity_type === 'client') {
      return `${entity.company_name} - ${entity.contact_person}`;
    } else if (formData.entity_type === 'lead' || formData.entity_type === 'opportunity') {
      return entity.name || entity.company || entity.id;
    } else if (formData.entity_type === 'contact') {
      return `${entity.name} - ${entity.email || entity.phone || ''}`;
    }
    return entity.id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#2B2D42]">Upload Document</h2>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#2B2D42]"
            disabled={uploading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              File * (Max 16MB)
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                required
                disabled={uploading}
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-[#6C757D]">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          </div>

          {/* Entity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Entity Type *
            </label>
            <select
              value={formData.entity_type}
              onChange={(e) => {
                setFormData({ ...formData, entity_type: e.target.value, entity_id: '' });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              required
              disabled={uploading}
            >
              {ENTITY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Select {ENTITY_TYPES.find(t => t.value === formData.entity_type)?.label} *
            </label>
            {loading ? (
              <p className="text-sm text-[#6C757D]">Loading...</p>
            ) : entities.length === 0 ? (
              <p className="text-sm text-red-600">No {formData.entity_type}s found. Please create one first.</p>
            ) : (
              <select
                value={formData.entity_id}
                onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                required
                disabled={uploading}
              >
                <option value="">Select {formData.entity_type}</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              disabled={uploading}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Optional description..."
              disabled={uploading}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., important, confidential (comma separated)"
              disabled={uploading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
